import { useEffect, useState, useCallback } from "react";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Search, Plus, X, FileDown, Loader2, Receipt, CheckCircle2, CreditCard, Copy, Send, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { SendQuoteDialog } from "@/components/SendQuoteDialog";

export function QuoteDialog({ open, onOpenChange, ticket, onCreated }) {
  const [settings, setSettings] = useState(null);
  const [q, setQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [items, setItems] = useState([]);
  const [labor, setLabor] = useState({ hours: 1, rate: null });
  const [discountPct, setDiscountPct] = useState(0);
  const [notes, setNotes] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [saving, setSaving] = useState(false);

  const loadSettings = useCallback(async () => {
    try {
      const { data } = await api.get("/settings");
      setSettings(data);
      setLabor((l) => ({ ...l, rate: data.labor_rate }));
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  useEffect(() => {
    if (open) {
      setItems([]); setQ(""); setSearchResults([]);
      setDiscountPct(0); setNotes(""); setLabor({ hours: 1, rate: null });
      setCustomerEmail(ticket?.reported_by_email || "");
      setCustomerPhone(ticket?.whatsapp_from || "");
      loadSettings();
    }
  }, [open, loadSettings, ticket]);

  const search = async () => {
    setSearching(true);
    try {
      const { data } = await api.get("/spare-parts", { params: { q: q.trim() || undefined, limit: 20 } });
      setSearchResults(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSearching(false); }
  };

  const addItem = (part) => {
    if (items.some((it) => it.code === part.code)) {
      setItems(items.map((it) => it.code === part.code ? { ...it, qty: it.qty + 1 } : it));
    } else {
      setItems([...items, { code: part.code, description: part.description, qty: 1, unit_price: part.price }]);
    }
  };
  const updateItem = (code, patch) => setItems(items.map((it) => it.code === code ? { ...it, ...patch } : it));
  const removeItem = (code) => setItems(items.filter((it) => it.code !== code));

  const subParts = items.reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.unit_price) || 0), 0);
  const rate = Number(labor.rate ?? settings?.labor_rate ?? 0);
  const subLabor = (Number(labor.hours) || 0) * rate;
  const subtotal = subParts + subLabor;
  const discount = subtotal * (Number(discountPct) / 100);
  const beforeVat = subtotal - discount;
  const vat = beforeVat * ((settings?.vat_pct || 0) / 100);
  const total = beforeVat + vat;
  const currency = settings?.currency || "EUR";
  const fmt = (n) => n.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const submit = async () => {
    if (items.length === 0 && !labor.hours) {
      toast.error("Aggiungi almeno un ricambio o le ore di manodopera");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ticket_id: ticket.id,
        customer_name: ticket.customer_name || null,
        customer_email: customerEmail.trim() || null,
        customer_phone: customerPhone.trim().replace(/^\+/, "") || null,
        dispenser_serial: ticket.dispenser_serial,
        items: items.map((it) => ({
          code: it.code, description: it.description,
          qty: parseFloat(it.qty) || 0,
          unit_price: parseFloat(it.unit_price) || 0,
        })),
        labor_hours: parseFloat(labor.hours) || 0,
        labor_rate: parseFloat(labor.rate) || 0,
        discount_pct: parseFloat(discountPct) || 0,
        notes: notes || null,
      };
      const { data } = await api.post("/quotes", payload);
      toast.success(`Preventivo ${data.quote_number} creato`);
      onCreated?.(data);
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight flex items-center gap-2">
            <Receipt className="h-5 w-5 text-brand" /> Nuovo preventivo
          </DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            Aggiungi ricambi e ore di manodopera, poi genera il PDF da inviare al cliente.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Search & pick */}
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-500">Cerca ricambio</Label>
            <div className="flex gap-2 mt-1">
              <Input
                data-testid="quote-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), search())}
                placeholder="Codice o descrizione (es. tastiera, SPR0193)"
                className="rounded-sm"
              />
              <Button data-testid="quote-search-btn" onClick={search} disabled={searching} className="bg-brand hover:bg-brand-dark text-white rounded-sm">
                {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              </Button>
            </div>
            {searchResults.length > 0 && (
              <div className="mt-2 border border-zinc-200 rounded-sm max-h-52 overflow-y-auto" data-testid="quote-search-results">
                {searchResults.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => addItem(p)}
                    className="w-full text-left px-3 py-2 hover:bg-zinc-50 border-b border-zinc-100 last:border-0 flex items-center justify-between"
                    data-testid={`quote-add-${p.code}`}
                  >
                    <div className="min-w-0">
                      <div className="font-mono text-xs text-zinc-900">{p.code}</div>
                      <div className="text-xs text-zinc-600 truncate">{p.description}</div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0 ml-3">
                      <span className="font-mono text-sm text-zinc-900">{fmt(p.price)} €</span>
                      <Plus className="h-4 w-4 text-brand" />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Selected items */}
          <div>
            <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Ricambi selezionati ({items.length})</div>
            <div className="border border-zinc-200 rounded-sm">
              {items.length === 0 && (
                <div className="p-4 text-xs text-zinc-500 text-center">Nessun ricambio. Cerca e aggiungi sopra.</div>
              )}
              {items.map((it) => (
                <div key={it.code} className="flex items-center gap-2 px-3 py-2 border-b border-zinc-100 last:border-0" data-testid={`quote-item-${it.code}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-xs text-zinc-900">{it.code}</div>
                    <div className="text-xs text-zinc-600 truncate">{it.description}</div>
                  </div>
                  <Input
                    type="number" step="1" min="0"
                    value={it.qty}
                    onChange={(e) => updateItem(it.code, { qty: e.target.value })}
                    className="w-16 h-8 rounded-sm text-sm"
                    data-testid={`quote-qty-${it.code}`}
                  />
                  <Input
                    type="number" step="0.01" min="0"
                    value={it.unit_price}
                    onChange={(e) => updateItem(it.code, { unit_price: e.target.value })}
                    className="w-24 h-8 rounded-sm text-sm font-mono"
                    data-testid={`quote-price-${it.code}`}
                  />
                  <div className="w-24 text-right font-mono text-sm text-zinc-900">
                    {fmt((Number(it.qty) || 0) * (Number(it.unit_price) || 0))} €
                  </div>
                  <button type="button" onClick={() => removeItem(it.code)} className="text-zinc-400 hover:text-red-600" data-testid={`quote-remove-${it.code}`}>
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Labor + discount */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Ore manodopera</Label>
              <Input data-testid="quote-labor-hours" type="number" step="0.25" min="0" value={labor.hours}
                onChange={(e) => setLabor({ ...labor, hours: e.target.value })} className="mt-1 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">€/h</Label>
              <Input data-testid="quote-labor-rate" type="number" step="0.01" min="0"
                value={labor.rate ?? ""}
                onChange={(e) => setLabor({ ...labor, rate: e.target.value })} className="mt-1 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Sconto %</Label>
              <Input data-testid="quote-discount" type="number" step="0.5" min="0" max="100"
                value={discountPct}
                onChange={(e) => setDiscountPct(e.target.value)} className="mt-1 rounded-sm" />
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-500">Note</Label>
            <Textarea data-testid="quote-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1 rounded-sm" placeholder="Note per il cliente (opzionale)" />
          </div>

          {/* Contatti cliente finale (per invio email/WhatsApp con link pagamento) */}
          <div className="border-t border-zinc-200 pt-3">
            <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">
              Contatti cliente finale <span className="normal-case text-[10px] text-zinc-400">(per invio del preventivo con link pagamento)</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Email</Label>
                <Input
                  type="email"
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                  placeholder="cliente@esempio.it"
                  className="mt-1 rounded-sm"
                  data-testid="quote-customer-email"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Telefono WhatsApp</Label>
                <Input
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(e.target.value)}
                  placeholder="es. 393331234567"
                  className="mt-1 rounded-sm font-mono"
                  data-testid="quote-customer-phone"
                />
              </div>
            </div>
          </div>

          {/* Totals */}
          <div className="rounded-sm border border-zinc-200 bg-zinc-50 p-3 space-y-1 text-sm" data-testid="quote-totals">
            <div className="flex justify-between"><span className="text-zinc-600">Subtotale ricambi</span><span className="font-mono">{fmt(subParts)} {currency}</span></div>
            <div className="flex justify-between"><span className="text-zinc-600">Subtotale manodopera</span><span className="font-mono">{fmt(subLabor)} {currency}</span></div>
            <div className="flex justify-between"><span className="text-zinc-600">Sconto ({discountPct || 0}%)</span><span className="font-mono">−{fmt(discount)} {currency}</span></div>
            <div className="flex justify-between border-t border-zinc-200 pt-1"><span className="text-zinc-600">Imponibile</span><span className="font-mono">{fmt(beforeVat)} {currency}</span></div>
            <div className="flex justify-between"><span className="text-zinc-600">IVA {settings?.vat_pct || 0}%</span><span className="font-mono">{fmt(vat)} {currency}</span></div>
            <div className="flex justify-between text-brand font-display font-bold text-lg pt-1 border-t border-zinc-300"><span>TOTALE</span><span className="font-mono" data-testid="quote-total-value">{fmt(total)} {currency}</span></div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button data-testid="quote-submit-btn" onClick={submit} disabled={saving} className="bg-brand hover:bg-brand-dark text-white rounded-sm">
            {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Creazione...</> : "Crea preventivo"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function QuotesList({ quotes, onRefresh }) {
  const [sendTarget, setSendTarget] = useState(null); // quote object when dialog open
  const [reconcileTarget, setReconcileTarget] = useState(null); // quote object for manual reconcile
  const [reconcileSession, setReconcileSession] = useState("");
  const [reconciling, setReconciling] = useState(false);
  const del = async (id) => {
    if (!window.confirm("Eliminare il preventivo?")) return;
    try {
      await api.delete(`/quotes/${id}`);
      toast.success("Preventivo eliminato");
      onRefresh?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const markExecuted = async (id) => {
    try {
      await api.put(`/quotes/${id}/execute`);
      toast.success("Preventivo segnato come eseguito");
      onRefresh?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const generatePaymentLink = async (id, quoteNumber) => {
    try {
      const { data } = await api.post(`/quotes/${id}/payment-link`, { origin_url: window.location.origin });
      await navigator.clipboard.writeText(data.url).catch(() => null);
      toast.success(`Link Stripe generato · €${data.amount.toFixed(2)} · copiato negli appunti`, { duration: 6000 });
      // Also open the link in a new tab so admin can preview/send
      window.open(data.url, "_blank", "noopener,noreferrer");
      onRefresh?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const verifyPayments = async (id, quoteNumber) => {
    // Polls Stripe for all txns of this quote — resolves the "webhook never fired" scenario.
    try {
      const { data: txns } = await api.get(`/quotes/${id}/payments`);
      if (!txns || txns.length === 0) {
        toast.info("Nessun link di pagamento generato per questo preventivo. Se hai un session_id Stripe, usa 'Riconcilia manualmente'.");
        return;
      }
      let paidFound = false;
      for (const t of txns) {
        if (t.payment_status === "paid") { paidFound = true; continue; }
        try {
          const { data } = await api.get(`/payments/status/${t.session_id}`);
          if (data.payment_status === "paid") paidFound = true;
        } catch (_) { /* skip */ }
      }
      if (paidFound) {
        toast.success(`Pagamento verificato per ${quoteNumber}`);
        onRefresh?.();
      } else {
        toast.info(`Nessun pagamento completato trovato per ${quoteNumber}`);
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const submitReconcile = async () => {
    if (!reconcileSession.trim()) return;
    setReconciling(true);
    try {
      const { data } = await api.post(`/payments/reconcile`, {
        session_id: reconcileSession.trim(),
        quote_id: reconcileTarget.id,
      });
      if (data.payment_status === "paid") {
        toast.success(`Pagamento riconciliato: preventivo segnato come pagato`);
        setReconcileTarget(null);
        setReconcileSession("");
        onRefresh?.();
      } else {
        toast.info(`Stato Stripe: ${data.payment_status || data.status || "non pagato"}`);
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setReconciling(false); }
  };
  if (!quotes || quotes.length === 0) {
    return <div className="text-xs text-zinc-500 py-2">Nessun preventivo. Creane uno per generare il PDF da inviare al cliente.</div>;
  }
  return (
    <div className="space-y-2" data-testid="quotes-list">
      {quotes.map((q) => {
        const executed = q.status === "eseguito";
        return (
        <div key={q.id} className={`rounded-sm border px-3 py-2 ${executed ? "border-emerald-200 bg-emerald-50/50" : "border-zinc-200"}`} data-testid={`quote-row-${q.quote_number}`}>
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-mono text-xs text-zinc-900">{q.quote_number}</span>
                {executed && (
                  <span className="text-[9px] uppercase tracking-widest bg-emerald-100 text-emerald-800 border border-emerald-200 rounded-sm px-1.5 py-0.5" data-testid={`quote-executed-badge-${q.quote_number}`}>
                    Eseguito
                  </span>
                )}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">
                {new Date(q.created_at).toLocaleDateString("it-IT")} · {q.items?.length || 0} ricambi · {q.labor_hours}h
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <div className="font-mono text-sm text-zinc-900 mr-1">
                {q.total?.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
              </div>
              <a
                href={`${API_BASE}/quotes/${q.id}/pdf`}
                target="_blank"
                rel="noreferrer"
                data-testid={`quote-pdf-${q.quote_number}`}
                className="inline-flex items-center gap-1 h-8 px-2 rounded-sm border border-zinc-300 text-xs text-zinc-800 hover:bg-zinc-50"
              >
                <FileDown className="h-3.5 w-3.5" /> PDF
              </a>
              {!executed && (
                <button
                  onClick={() => markExecuted(q.id)}
                  className="inline-flex items-center gap-1 h-8 px-2 rounded-sm border border-emerald-300 text-xs text-emerald-800 hover:bg-emerald-50"
                  data-testid={`quote-execute-${q.quote_number}`}
                  title="Segna i ricambi come utilizzati"
                >
                  <CheckCircle2 className="h-3.5 w-3.5" /> Eseguito
                </button>
              )}
              {q.payment_status !== "paid" && (
                <button
                  onClick={() => setSendTarget(q)}
                  className="inline-flex items-center gap-1 h-8 px-2 rounded-sm border border-brand text-xs text-brand hover:bg-brand-muted"
                  data-testid={`quote-send-${q.quote_number}`}
                  title="Invia preventivo al cliente via email/WhatsApp"
                >
                  <Send className="h-3.5 w-3.5" /> Invia
                </button>
              )}
              {q.payment_status !== "paid" && (
                <button
                  onClick={() => generatePaymentLink(q.id, q.quote_number)}
                  className="inline-flex items-center gap-1 h-8 px-2 rounded-sm border border-brand/60 text-xs text-brand hover:bg-brand-muted"
                  data-testid={`quote-payment-${q.quote_number}`}
                  title="Genera link Stripe (senza inviarlo al cliente)"
                >
                  <CreditCard className="h-3.5 w-3.5" /> Link
                </button>
              )}
              {q.payment_status !== "paid" && (
                <button
                  onClick={() => verifyPayments(q.id, q.quote_number)}
                  className="inline-flex items-center gap-1 h-8 px-2 rounded-sm border border-zinc-300 text-xs text-zinc-700 hover:bg-zinc-50"
                  data-testid={`quote-verify-${q.quote_number}`}
                  title="Verifica se il cliente ha pagato (interroga Stripe)"
                >
                  <RefreshCw className="h-3.5 w-3.5" /> Verifica
                </button>
              )}
              {q.payment_status !== "paid" && (
                <button
                  onClick={() => { setReconcileTarget(q); setReconcileSession(""); }}
                  className="inline-flex items-center gap-1 h-8 px-2 rounded-sm border border-orange-300 text-xs text-orange-700 hover:bg-orange-50"
                  data-testid={`quote-reconcile-${q.quote_number}`}
                  title="Riconcilia manualmente inserendo il session_id di Stripe"
                >
                  ⚙ Riconcilia
                </button>
              )}
              {q.payment_status === "paid" && (
                <span className="inline-flex items-center gap-1 h-8 px-2 rounded-sm border border-emerald-300 bg-emerald-50 text-xs text-emerald-800 font-semibold" data-testid={`quote-paid-badge-${q.quote_number}`}>
                  <CheckCircle2 className="h-3.5 w-3.5" /> Pagato
                </span>
              )}
              <button
                onClick={() => del(q.id)}
                className="h-8 w-8 rounded-sm border border-zinc-300 text-zinc-400 hover:text-red-600 hover:border-red-200 flex items-center justify-center"
                data-testid={`quote-delete-${q.quote_number}`}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </div>
        );
      })}
      {sendTarget && (
        <SendQuoteDialog
          open={!!sendTarget}
          onOpenChange={(v) => { if (!v) setSendTarget(null); }}
          quote={sendTarget}
          onSent={() => { onRefresh?.(); }}
        />
      )}
      <Dialog open={!!reconcileTarget} onOpenChange={(v) => { if (!v) { setReconcileTarget(null); setReconcileSession(""); } }}>
        <DialogContent className="rounded-sm max-w-md" data-testid="reconcile-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Riconcilia pagamento Stripe</DialogTitle>
            <DialogDescription>
              Preventivo <span className="font-mono">{reconcileTarget?.quote_number}</span> · €{reconcileTarget?.total?.toFixed(2)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-zinc-600">
              Se il cliente ha pagato ma il preventivo risulta ancora "non pagato" (webhook Stripe fallito o non
              registrato), incolla qui il <b>Session ID</b> del pagamento (formato <code className="font-mono">cs_live_...</code>).
              Lo trovi sul dashboard Stripe → Payments → aprire il pagamento → sezione "Checkout session".
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Session ID Stripe</Label>
              <Input
                value={reconcileSession}
                onChange={(e) => setReconcileSession(e.target.value)}
                placeholder="cs_live_a1b2c3..."
                className="mt-1 rounded-sm font-mono text-xs"
                data-testid="reconcile-session-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => { setReconcileTarget(null); setReconcileSession(""); }}>Annulla</Button>
            <Button
              onClick={submitReconcile}
              disabled={reconciling || !reconcileSession.trim()}
              className="bg-brand hover:bg-brand-dark text-white rounded-sm"
              data-testid="reconcile-submit"
            >
              {reconciling ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Verifica...</> : "Verifica e riconcilia"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

